package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareService
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ShareConfig
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.GemArt
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.gemArtImage
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.core.model.Poem
import diana.soleil.design.iraniancanadienbazzar.core.model.Poet
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_copied
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_error
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_fal_draw
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_fal_draw_again
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_fal_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_fal_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_fal_prompt_body
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_fal_prompt_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_fal_reading_label
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_fal_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_poem_translation
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_share_message
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import kotlin.random.Random

/*
 * Fal-e Hafez (فال حافظ): hold a question in mind, open the Divan at random, and read the ghazal
 * that answers. Families do this on Yalda night and at Nowruz; here it is one tap. The draw pool is
 * every published Hafez poem that carries a `fal` reading, so a poem without one is never dealt.
 */

/** Poets are keyed by a stable corpus id; the fal only ever draws from this one. */
internal const val HAFEZ_POET_ID = "poet-hafez"

data class FalUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    /** How many readings are in the pool — zero shows the "Divan is closed" empty state. */
    val available: Int = 0,
    val drawn: Poem? = null,
    val poet: Poet? = null,
    val liked: Boolean = false,
    val saved: Boolean = false,
    val signInRequired: Boolean = false,
    val reportStatus: GemReportStatus = GemReportStatus.Idle,
)

class FalViewModel(
    private val gemsRepository: GemsRepository,
    private val savedController: GemsSavedController,
    private val reportRepository: ReportRepository,
) : ViewModel() {

    private val drawnId = MutableStateFlow<String?>(null)
    private val signInGate = MutableStateFlow(false)
    private val reportStatus = MutableStateFlow(GemReportStatus.Idle)

    // The pool + poet ride an inner combine: the typed overloads stop at five sources.
    private val content = combine(
        gemsRepository.observePoems(HAFEZ_POET_ID),
        gemsRepository.observePoets(),
        drawnId,
    ) { poemsResult, poetsResult, id ->
        Triple(poemsResult, (poetsResult as? DataResult.Success)?.data?.firstOrNull { it.id == HAFEZ_POET_ID }, id)
    }

    val state: StateFlow<FalUiState> =
        combine(
            content,
            gemsRepository.observeLikedPoemIds(),
            savedController.saved,
            signInGate,
            reportStatus,
        ) { (poemsResult, poet, id), liked, saved, signIn, report ->
            when (poemsResult) {
                is DataResult.Failure -> FalUiState(loading = false, error = true, signInRequired = signIn, reportStatus = report)
                is DataResult.Success -> {
                    val pool = poemsResult.data.filter { it.fal != null }
                    val drawn = pool.firstOrNull { it.id == id }
                    FalUiState(
                        loading = false,
                        available = pool.size,
                        drawn = drawn,
                        poet = poet,
                        liked = drawn != null && drawn.id in liked,
                        saved = drawn != null && saved.any { it.type == GemType.Poem && it.id == drawn.id },
                        signInRequired = signIn,
                        reportStatus = report,
                    )
                }
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), FalUiState())

    /**
     * Opens the Divan: a uniform draw over the pool, never the same ghazal twice in a row when there
     * is a choice — asking again and getting the identical page reads as a broken button.
     */
    fun draw() {
        viewModelScope.launch {
            // The first emission is the current snapshot (cache or server) — enough to deal from.
            val result = gemsRepository.observePoems(HAFEZ_POET_ID).first()
            val pool = (result as? DataResult.Success)?.data.orEmpty().filter { it.fal != null }
            if (pool.isEmpty()) return@launch
            val candidates = if (pool.size > 1) pool.filter { it.id != drawnId.value } else pool
            drawnId.value = candidates[Random.nextInt(candidates.size)].id
        }
    }

    fun toggleLike() {
        val poem = state.value.drawn ?: return
        val liked = state.value.liked
        viewModelScope.launch {
            val result = gemsRepository.setPoemLiked(poem.id, !liked)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) signInGate.value = true
        }
    }

    fun toggleSave() {
        val poem = state.value.drawn ?: return
        viewModelScope.launch { savedController.toggle(SavedGem(id = poem.id, type = GemType.Poem)) }
    }

    fun consumeSignInGate() { signInGate.value = false }

    fun report(reason: String) {
        val poem = state.value.drawn ?: return
        if (reportStatus.value == GemReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = GemReportStatus.Sending
            reportStatus.value = when (val r = reportRepository.fileReport("poem", poem.id, reason)) {
                is DataResult.Success -> GemReportStatus.Sent
                is DataResult.Failure ->
                    if (r.error is AppError.Unauthorized) GemReportStatus.SignInRequired else GemReportStatus.Failed
            }
        }
    }

    fun resetReport() { reportStatus.value = GemReportStatus.Idle }
}

/** Stateful entry for Fal-e Hafez (`/gems/fal`). */
@Composable
fun FalRoute(
    onBack: () -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: FalViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val shareService = koinInject<ShareService>()
    val shareConfig = koinInject<ShareConfig>()
    val lang = AppTheme.language
    LaunchedEffect(state.signInRequired) {
        if (state.signInRequired) { viewModel.consumeSignInGate(); onRequireSignIn() }
    }
    LaunchedEffect(state.reportStatus) {
        if (state.reportStatus == GemReportStatus.SignInRequired) { viewModel.resetReport(); onRequireSignIn() }
    }
    val shareTemplate = stringResource(Res.string.gems_share_message)
    FalScreen(
        state = state,
        onBack = onBack,
        onDraw = viewModel::draw,
        onToggleLike = viewModel::toggleLike,
        onToggleSave = viewModel::toggleSave,
        onShare = { poem ->
            shareService.share(
                shareTemplate
                    .replace("%1\$s", poem.title.resolve(lang))
                    .replace("%2\$s", shareConfig.poemLink(poem.id)),
            )
        },
        onReport = viewModel::report,
        onResetReport = viewModel::resetReport,
        modifier = modifier,
    )
}

/**
 * Two faces: the intention prompt (art, a line of instruction, one big button) and the drawn page
 * (byline → verse panel → translation → the reading on a teal card → actions → ask again).
 */
@Composable
fun FalScreen(
    state: FalUiState,
    onBack: () -> Unit,
    onDraw: () -> Unit,
    onToggleLike: () -> Unit,
    onToggleSave: () -> Unit,
    onShare: (Poem) -> Unit,
    onReport: (reason: String) -> Unit,
    onResetReport: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    val clipboard = LocalClipboardManager.current
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val copiedMessage = stringResource(Res.string.gems_copied)
    var showReport by remember { mutableStateOf(false) }

    Box(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        Column(
            modifier = Modifier.fillMaxSize().statusBarsPadding().navigationBarsPadding(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            GemsSubTopBar(title = stringResource(Res.string.gems_fal_title), onBack = onBack)
            when {
                state.loading -> LoadingState()
                state.error -> EmptyState(title = stringResource(Res.string.gems_error))
                state.available == 0 -> EmptyState(
                    title = stringResource(Res.string.gems_fal_empty_title),
                    description = stringResource(Res.string.gems_fal_empty_desc),
                )
                state.drawn == null -> IntentionPrompt(onDraw = onDraw)
                else -> {
                    val poem = state.drawn
                    val persian = poem.body.fa?.lines()?.filter { it.isNotBlank() }.orEmpty()
                    val translation = poem.body.resolve(lang).takeIf { lang != Language.FA && persian.isNotEmpty() }
                    Column(
                        modifier = Modifier
                            .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                            .fillMaxWidth()
                            .verticalScroll(rememberScrollState())
                            .padding(AppTheme.spacing.lg),
                        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
                    ) {
                        Text(
                            text = poem.title.resolve(lang),
                            style = AppTheme.typography.headlineMd,
                            color = AppTheme.colors.onSurface,
                        )
                        GemByline(
                            parts = listOf(
                                state.poet?.name?.resolve(lang).orEmpty(),
                                state.poet?.era?.resolve(lang).orEmpty(),
                                poem.form.label(),
                            ),
                        )
                        if (persian.isNotEmpty()) {
                            PersianVersePanel(lines = persian)
                        } else {
                            Text(text = poem.body.resolve(lang), style = AppTheme.typography.bodyLg, color = AppTheme.colors.onSurface)
                        }
                        translation?.let {
                            Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
                                Text(
                                    text = stringResource(Res.string.gems_poem_translation),
                                    style = AppTheme.typography.labelSm,
                                    color = AppTheme.colors.onSurfaceVariant,
                                )
                                Text(text = it, style = AppTheme.typography.bodyLg, color = AppTheme.colors.onSurface)
                            }
                        }
                        // The reading — same card the story moral uses, so the family stays one system.
                        poem.fal?.resolve(lang)?.takeIf { it.isNotBlank() }?.let { reading ->
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(AppTheme.colors.primaryContainer, AppTheme.shapes.card)
                                    .padding(AppTheme.spacing.md),
                                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                            ) {
                                Text(
                                    text = stringResource(Res.string.gems_fal_reading_label),
                                    style = AppTheme.typography.labelSm,
                                    color = AppTheme.colors.onPrimaryContainer,
                                )
                                Text(text = reading, style = AppTheme.typography.bodyLg, color = AppTheme.colors.onPrimary)
                            }
                        }
                        GemDetailRail(
                            likeCount = poem.likeCount,
                            liked = state.liked,
                            saved = state.saved,
                            onToggleLike = onToggleLike,
                            onToggleSave = onToggleSave,
                            onCopy = {
                                clipboard.setText(AnnotatedString(poem.body.resolve(lang)))
                                scope.launch { snackbarHostState.showSnackbar(copiedMessage) }
                            },
                            onShare = { onShare(poem) },
                            onReport = { showReport = true },
                        )
                        AppButton(
                            text = stringResource(Res.string.gems_fal_draw_again),
                            onClick = onDraw,
                            variant = AppButtonVariant.Secondary,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
            }
        }
        SnackbarHost(hostState = snackbarHostState, modifier = Modifier.align(Alignment.BottomCenter))
    }

    if (showReport) {
        GemReportSheet(
            status = state.reportStatus,
            onSubmit = onReport,
            onDismiss = { showReport = false; onResetReport() },
        )
    }
}

/** The closed Divan: art, the instruction, and the one button that opens it. */
@Composable
private fun IntentionPrompt(onDraw: () -> Unit) {
    Column(
        modifier = Modifier
            .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(AppTheme.spacing.lg),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        Spacer(modifier = Modifier.height(AppTheme.spacing.xxl))
        Image(
            painter = painterResource(gemArtImage(GemArt.Hub)),
            contentDescription = null,
            modifier = Modifier.size(AppTheme.sizes.brandMark),
        )
        Text(
            text = stringResource(Res.string.gems_fal_prompt_title),
            style = AppTheme.typography.headlineLg,
            color = AppTheme.colors.onSurface,
            textAlign = TextAlign.Center,
        )
        Text(
            text = stringResource(Res.string.gems_fal_prompt_body),
            style = AppTheme.typography.bodyLg,
            color = AppTheme.colors.onSurfaceVariant,
            textAlign = TextAlign.Center,
        )
        Spacer(modifier = Modifier.height(AppTheme.spacing.md))
        AppButton(
            text = stringResource(Res.string.gems_fal_draw),
            onClick = onDraw,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}
